import _ from "lodash"
import React from "react"
import styled from "styled-components"
import { Link, graphql, useStaticQuery } from "gatsby"
import AppleStoreLogo from "../../images/icons/apple-icon.png"
import GoogleStoreLogo from "../../images/icons/play-logo.png"
import MMSmallLogo from "../../images/icons/small-logo-footer.png"
import { ScrollLink } from "../elements"

const FooterContainer = styled.footer`
  position: relative;
  z-index: 2;
  background-color: var(--text-dark);
  color: var(--text-light);
  padding: 50px var(--gutter-s) var(--gutter-m);
  @media (${({ theme }): string => theme.screenSizes.sm}) {
    padding: 100px var(--gutter-s) var(--gutter-m);
  }
`

const SectionLinksWrapper = styled.div`
  @media (${({ theme }): string => theme.screenSizes.sm}) {
    display: flex;
    justify-content: center;
  }
`

const SectionLinks = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 50px;

  &:last-child {
    margin-right: 0;
  }

  @media (${({ theme }): string => theme.screenSizes.sm}) {
    align-items: flex-start;
    margin-bottom: 0px;
    margin-right: 60px;

    &:nth-child(2) {
      margin-right: 120px;
    }
  }

  @media (${({ theme }): string => theme.screenSizes.md}) {
    margin-right: 150px;

    &:nth-child(2) {
      margin-right: 200px;
    }
  }
`

const SectionTitle = styled.h3`
  text-transform: uppercase;
  font-size: 24px;
  font-weight: 400;
  margin-bottom: 30px;
`

const SectionLink = styled(Link)`
  text-transform: uppercase;
  margin-bottom: 15px;
  font-size: 15px;
`

const ExternalLink = styled.a`
  text-transform: uppercase;
  margin-bottom: 15px;
  font-size: 15px;
`

const SectionScrollLink = styled(ScrollLink)`
  text-transform: uppercase;
  margin-bottom: 15px;
  font-size: 15px;
`

const AppLinks = styled.div`
  margin-top: 60px;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (${({ theme }): string => theme.screenSizes.sm}) {
    margin-top: 200px;
  }
`

const AppleStoreLink = styled.a`
  margin-right: 30px;

  img {
    height: 32px;
  }
`

const GoogleStoreLink = styled.a`
  img {
    height: 32px;
  }
`

const CopyrightText = styled.p`
  text-align: center;
  font-size: 14px;
  text-transform: uppercase;
  color: #4a4a4a;

  img {
    vertical-align: middle;
    height: 20px;
    margin-right: 15px;
    margin-bottom: 2px;
  }
`

const Footer: React.FC = () => {
  const data = useStaticQuery(graphql`
    query {
      apartmentsData: allApartmentsJson {
        edges {
          node {
            type
            link
            order
          }
        }
      }
    }
  `)

  const apartments = _.sortBy(
    _.map(data.apartmentsData.edges, apartmentEdge => ({
      order: apartmentEdge?.node?.order ?? 0,
      type: apartmentEdge?.node?.type ?? "",
      link: apartmentEdge?.node?.link ?? "",
    })),
    "order"
  )

  return (
    <FooterContainer>
      <SectionLinksWrapper>
        <SectionLinks>
          <SectionTitle>Navigate</SectionTitle>

          <SectionScrollLink page="/" section="apartments">
            Apartments for sale
          </SectionScrollLink>
          <SectionScrollLink page="/" section="neighbourhood">
            Our neighbourhood
          </SectionScrollLink>
          <SectionScrollLink page="/" section="technology">
            Technology
          </SectionScrollLink>
          <SectionLink to="/contact-us">Contact us</SectionLink>
        </SectionLinks>

        <SectionLinks>
          <SectionTitle>Apartments for sale</SectionTitle>
          {_.map(apartments, apartment => (
            <SectionLink key={apartment.link} to={apartment.link}>
              {apartment.type}
            </SectionLink>
          ))}
        </SectionLinks>

        <SectionLinks>
          <SectionTitle>Legal</SectionTitle>

          <SectionLink to="/privacy">Privacy</SectionLink>
          <SectionLink to="/terms">Terms of use</SectionLink>
          <ExternalLink
            href="https://sacoronavirus.co.za/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Covid-19
          </ExternalLink>
        </SectionLinks>

        <SectionLinks>
          <SectionTitle>Social</SectionTitle>

          <ExternalLink
            href="https://www.facebook.com/MenlynMaine/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Facebook
          </ExternalLink>
          <ExternalLink
            href="https://www.instagram.com/menlynmaine/?hl=en"
            target="_blank"
            rel="noopener noreferrer"
          >
            Instagram
          </ExternalLink>
        </SectionLinks>
      </SectionLinksWrapper>

      <AppLinks>
        <AppleStoreLink
          href="https://apps.apple.com/za/app/menlyn-maine/id1416644613"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src={AppleStoreLogo}
            alt="Menlyn Maine app - download from App store"
          />
        </AppleStoreLink>
        <GoogleStoreLink
          href="https://play.google.com/store/apps/details?id=com.menlynmaine&hl=en_ZA"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src={GoogleStoreLogo}
            alt="Menlyn Maine app - download from App store"
          />
        </GoogleStoreLink>
      </AppLinks>

      <CopyrightText>
        <img src={MMSmallLogo} alt="Menlyn Maine logo" />
        {new Date().getFullYear()}&copy; Menlyn Maine. All rights reserved
      </CopyrightText>
    </FooterContainer>
  )
}

export default Footer
